import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { BaseProductDetailComponent } from '@components/products/base-product-detail.component';
import { FundCompareDetail, } from '@components/products/fund-listing/fund-compare/fund-compare.type';
import { FundListingBRConfiguration, FundListingComponentType, FundListingDataFormat, FundListingState } from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { FundListingDisplayComponent } from '../fund-listing-display-component.interface';
import { FundListingService } from '../services/fund-listing.service';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import { TranslateService } from '@components/shared/translate/translate.service';
import { Router } from '@angular/router';

const logger = Logger.getLogger('HomeFundListingComponent');

@Component({
  selector: 'ft-home-fund-listing',
  templateUrl: './home-fund-listing.component.html',
  styleUrls: ['./home-fund-listing.component.scss'],
})
export class HomeFundListingComponent
  extends BaseProductDetailComponent
  implements FundListingDisplayComponent, OnInit, OnDestroy {

  brConfiguration: FundListingBRConfiguration;
  dataSubscription: Subscription;
  componentType: FundListingComponentType;
  fundListingState: FundListingState;
  fundListingData: FundListingDataFormat[] | FundListingDataFormat[];
  fundCompareDetails: FundCompareDetail[] = [];
  focusFunds: any = [];
  funds: any[] = [];
  selectedAssetClass: string = 'All Funds';
  sideFilterOptions: any;
  isLoaded: boolean;
  badgeValue: string;
  focusTabName: string;
  isMobileView: boolean = false;

  slideConfig: any = {
    dots: false,
    arrows: false,
    autoplay: false,
    slidesToShow: 1,
    infinite: false,
    responsive: [
      {
        breakpoint: 9999,
        settings: 'unslick',
      },
      {
        breakpoint: 768,

        settings: {
          centerMode: true,
          arrows: false,
          slidesToShow: 1,
          centerPadding: '5%',
          slidesToScroll: 1,
        },
      },
    ],
  };

  @ViewChild('slickModal')
  slickModal!: SlickCarouselComponent;

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.initFundSlickCarousel();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event: any) {
    this.isMobileView = window.innerWidth < 768;
  }

  initFundSlickCarousel() {
    if (this.slickModal !== undefined) {
      if (window.innerWidth > 768) {
        if (this.slickModal.initialized) {
          this.slickModal.unslick();
        }
      } else if (!this.slickModal.initialized) {
        this.slickModal.initSlick();
      }
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private fundListingService: FundListingService,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.badgeValue = this.translateService.instant("products.focused-funds-badge");
    this.focusTabName = this.translateService.instant("products.home-fund-listing-focus-tab-name");
    let focusFunds = this.translateService.instant("products.focused-funds-config");
    const focusFundsArray = focusFunds?.split(",");
    this.focusFunds = focusFundsArray?.map(number => {
      return {"fundId": number};
    });
  }

  /**
   * Initialized when current commponent gets loaded.
   * @param brConfiguration bloomreach configuration received
   */
  populate(brConfiguration: FundListingBRConfiguration): void {
    // Save BR configuration only once.
    if (!this.brConfiguration) {
      this.brConfiguration = brConfiguration;
      this.brConfiguration.config.componentType = FundListingComponentType.PPSS;
    }

    this.dataSubscription = this.fundListingService
      .getFundInformationState$()
      ?.subscribe(
        async (state) => {
          logger.debug('Mapped fund listing state: ', state);
          this.isLoaded = state.isLoaded;
          if (this.isLoaded) {
            this.componentType = this.brConfiguration.config.componentType;
            this.fundListingState = state;
            // side Options
            this.sideFilterOptions = state.filterDropdowns[0].filterItems.map((fund, index) => ({
              id: (index + 1).toString(),
              name: fund.label
            }));
            this.fundListingData = [];
            this.customizeFundData();
          }
          this.cdr.detectChanges();
        },
        (error) => {
          logger.error('getFundInformationState$: Error in invoking fund listing service: ', error);
          this.cdr.detectChanges();
        }
      );

    this.fundListingService.populate(this.brConfiguration);
  }

  customizeFundData(): void {
    let modifiedData = this.fundListingState?.ppssState?.ppssData ? JSON.parse(JSON.stringify(this.fundListingState?.ppssState?.ppssData)) : [];
    //console.log(modifiedData);
    // Sort the array based on averageAnnualReturnSinceInception
    modifiedData.sort((a, b) => {
      const returnA = parseFloat(a?.shareClassData?.performanceData?.averageAnnualReturnSinceInception);
      const returnB = parseFloat(b?.shareClassData?.performanceData?.averageAnnualReturnSinceInception);

      // Check if the parsed values are valid numbers
      const isValidNumberA = !isNaN(returnA);
      const isValidNumberB = !isNaN(returnB);

      // Handle cases where either or both values are not valid numbers
      if (isValidNumberA && isValidNumberB) {
        // Sort based on valid numeric values
        return returnB - returnA;
      } else if (isValidNumberA && !isValidNumberB) {
        // Sort valid numbers before non-valid numbers
        return -1;
      } else if (!isValidNumberA && isValidNumberB) {
        // Sort valid numbers before non-valid numbers
        return 1;
      } else {
        // Maintain the current order if neither value is a valid number
        return 0;
      }
    });
    for (let i = 0; i < modifiedData.length; i++) {
      let selectedFund = modifiedData[i];

      // Find the object with value "averageAnnualReturnSinceInception" in ppssPeriodDropdownItems
      // Add selectedDropdown to the main object
      selectedFund.selectedDropdown = selectedFund.ppssPeriodDropdownItems.find(item => item.value === "averageAnnualReturnSinceInception");
    }
    // Adding the Focus Badge.
    this.fundListingData = modifiedData.map(fund => {
      // Check if the fundId is in focusFunds array
      if (this.focusFunds.some(focusFund => focusFund.fundId === fund.fundId)) {
        // Add the badge property
        return {...fund, badge: this.badgeValue ? this.badgeValue : 'focus'};
      }
      return fund;
    });

    // Sort the array based on badge and prioritize funds with badge: focus
    this.fundListingData.sort((a, b) => {
      // Check if funds have the badge: focus property
      const aHasFocusBadge = a.badge === this.badgeValue;
      const bHasFocusBadge = b.badge === this.badgeValue;

      // Prioritize funds with the badge: focus property
      if (aHasFocusBadge && !bHasFocusBadge) {
        return -1;
      } else if (!aHasFocusBadge && bHasFocusBadge) {
        return 1;
      }

      // If both funds have the badge: focus property or neither does,
      // maintain the current order in the array
      return 0;
    });

  }

  getData(event: any) {
    const selectedFund = event.target.id;
    if (selectedFund) {
      this.selectedAssetClass = (selectedFund === 'All') ? 'All Funds' : selectedFund;
      //document.getElementById(selectedFund)?.click();
      this.fundListingData = [];
      this.customizeFundData();
      if (selectedFund !== 'All') {
        this.fundListingData = this.fundListingData.filter(fund => fund.assetClass === selectedFund);
      }
      if (this.isMobileView) {
        /*if (this.slickModal.initialized) {
          this.slickModal.unslick();
        }*/
        setTimeout(() => {
          if (!this.slickModal.initialized) {
            this.slickModal.initSlick();
          }
        }, 500);
      }
    }
  }

  gotoPage(param: string): void {
    const assetClass = param.toLowerCase();
    const allLinks = JSON.parse(this.translateService.instant("products.home-fund-card-all-links"));
    const keyMap: Record<string, string> = {
      "all funds": "home-fund-all-funds-link",
      "focus funds": "home-fund-focus-funds-link",
      "elss fund": "home-fund-elss-funds-link",
      "equity funds": "home-fund-equity-funds-link",
      "international funds": "home-fund-international-funds-link",
      "fixed income funds": "home-fund-fixed-income-funds-link",
      "fof domestic": "home-fund-fof-domestic-funds-link",
      "liquid funds": "home-fund-liquid-funds-link",
      "hybrid funds": "home-fund-hybrid-funds-link"
    };

    const link = this.getValueByKey(keyMap[assetClass], allLinks);

    if (link) {
      this.router.navigateByUrl(link);
    } else {
      logger.error(`Link not found for asset class: ${assetClass}`);
    }
  }

  getValueByKey(key: string, links: { key: string; value: string }[]): string | null {
    const item = links.find(link => link.key === key);
    return item ? item.value : null;
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription?.unsubscribe();
    }
  }
}
